import React, { useState, useEffect } from 'react'
import { ConfirmationDialog } from 'uiComponents/popups/confirmationDialog'
import { UpdateTicketValidToDetails, Ticket } from 'orders/schema'
import { OrdersService } from 'orders/ordersService'
import { stubFalse } from 'lodash'
import { DateFormats, flatMap, formatOrInvalid } from 'utils'
import { showErrorMessage } from 'uiComponents/message'
import { useTranslation, Trans } from 'translations'

interface UpdateDialogProps {
    accountSlug: string
    updateValidToDetails: UpdateTicketValidToDetails
    ordersService: OrdersService
    loading: boolean
    onCancel: () => void
    onConfirm: () => void
}

export function UpdateDialog(props: UpdateDialogProps) {
    const { t } = useTranslation()
    const defaultMessage = t('orders.details.updateDialog.confirmText', {
        date: formatOrInvalid(props.updateValidToDetails.date, DateFormats.EXTRA_LONG_DATE),
    })
    const [loading, setLoading] = useState<boolean>(false)
    const [text, setText] = useState<React.ReactNode>(defaultMessage)

    useEffect(() => {
        getOrderData()
    }, [])

    async function getOrderData() {
        setLoading(true)
        try {
            const order = await props.ordersService.getOrderDetailsForRefundOld(
                props.accountSlug,
                props.updateValidToDetails.orderId,
            )
            const details = props.updateValidToDetails
            const orderItemId = details.bundleOrderItemId || details.orderItemId
            const ticket = order.tickets.find((orderTicket) => orderTicket.orderItemId === Number(orderItemId))
            checkTicketItems(ticket)
        } catch {
            closeWithError()
        } finally {
            setLoading(stubFalse)
        }
    }

    function checkTicketItems(orderItem?: Ticket) {
        if (!orderItem) {
            closeWithError()
        } else if (orderItem.quantity < 2) {
            return
        } else {
            const flatMappedTickets = flatMap(orderItem.groupedTicketEntities, (x) => x)
            const barcodes: string[] = []
            flatMappedTickets.forEach((ticketEntity) => {
                if (ticketEntity.barcode?.barcode) {
                    barcodes.push(ticketEntity.barcode.barcode)
                }
            })
            setText(
                <>
                    {defaultMessage}
                    <div>
                        <Trans
                            i18nKey="orders.details.updateDialog.sameTicketsNote"
                            values={{ total: flatMappedTickets.length }}
                            components={{ b: <strong /> }}
                        />
                        {barcodes.length > 0 && (
                            <> {t('orders.details.updateDialog.barcodes', { barcodes: barcodes.join(', ') })}</>
                        )}
                    </div>
                </>,
            )
        }
    }

    function closeWithError() {
        showErrorMessage(t('orders.details.updateDialog.fetchError'), 'get_ticket_details_error')
        props.onCancel()
    }

    return (
        <ConfirmationDialog
            title={t('orders.details.updateDialog.title')}
            text={text}
            buttonText={t('orders.details.updateDialog.confirmButton')}
            onCancel={props.onCancel}
            onConfirm={props.onConfirm}
            loading={loading || props.loading}
        />
    )
}
